<div class="job-form-page" (click)="closeTooltip()">
  <div class="job-form-title-bar">
    <div class="title">{{ isEditMode() ? 'Job Detail' : 'Create New Job' }}</div>
  </div>

  <div class="job-form-wrapper">
    @if (isLoading) {
    <div class="empty-state">Loading job...</div>
    } @else {
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="job-form-container">
      <input #folderInput type="file" hidden webkitdirectory directory multiple (change)="onFolderSelected($event)" />

      <!-- Section 1: Job Information -->
      <section class="section-card">
        <h3>Job Information</h3>
        <div class="job-info-grid">
          <div class="field-col">
            <label class="field-label">Job Name *</label>
            <input type="text" formControlName="name" placeholder="e.g., Spring School Graduation 2026"
              class="text-input" />
            @if (form.get('name')?.hasError('required') && (form.get('name')?.dirty || form.get('name')?.touched)) {
            <div class="field-error">Job Name is required.</div>
            }
          </div>

          <div class="field-col">
            <label class="field-label">Job Folder *</label>
            <div class="folder-input-row">
              <input type="text" [value]="selectedFolderName" readonly placeholder="No folder selected"
                class="text-input folder-display-input" (click)="onSelectFolder()" />
              <button type="button" class="secondary-btn" [class.unverified]="!hasVerifiedFolder"
                (click)="onSelectFolder()">
                Select Job Folder
              </button>
            </div>
            @if (folderValidationMessage) {
            <div class="field-error">{{ folderValidationMessage }}</div>
            }
          </div>
        </div>

        <div class="job-stats-grid">
          <div class="stat-col">
            <label class="field-label">
              <span>Total Images Detected</span>
              <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                <span class="tooltip-icon" (click)="toggleTooltip('totalImages', $event)">ⓘ</span>
                @if (activeTooltipKey === 'totalImages') {
                <span class="tooltip-bubble">{{ tooltips['totalImages'] }}</span>
                }
              </span>
            </label>
            <input type="text" [value]="(form.get('noOfImagesInThisJob')?.value | number) ?? '0'" readonly
              class="stat-input readonly" />
            <div class="field-helper">Automatically detected from your selected folder.</div>
          </div>

          <div class="stat-col">
            <label class="field-label">
              <span>Approximate Subjects *</span>
              <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                <span class="tooltip-icon" (click)="toggleTooltip('approximateSubjects', $event)">ⓘ</span>
                @if (activeTooltipKey === 'approximateSubjects') {
                <span class="tooltip-bubble">{{ tooltips['approximateSubjects'] }}</span>
                }
              </span>
            </label>
            <input type="number" formControlName="estimatedStudentsPhotographed" min="1" class="stat-input" />
            <div class="field-helper">Enter your best estimate of the number of individual subjects in this job.</div>
            @if (form.get('estimatedStudentsPhotographed')?.hasError('required') &&
            (form.get('estimatedStudentsPhotographed')?.dirty || form.get('estimatedStudentsPhotographed')?.touched)) {
            <div class="field-error">Approximate Subjects is required.</div>
            }
            @if (form.get('estimatedStudentsPhotographed')?.hasError('min') &&
            (form.get('estimatedStudentsPhotographed')?.dirty || form.get('estimatedStudentsPhotographed')?.touched)) {
            <div class="field-error">Must be at least 1.</div>
            }
          </div>

          <div class="stat-col">
            <label class="field-label">
              <span>Estimated Average Images Per Subject</span>
              <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                <span class="tooltip-icon" (click)="toggleTooltip('estimatedAverageImages', $event)">ⓘ</span>
                @if (activeTooltipKey === 'estimatedAverageImages') {
                <span class="tooltip-bubble">{{ tooltips['estimatedAverageImages'] }}</span>
                }
              </span>
            </label>
            <input type="text" [value]="(form.get('avgFramesPhotographedPerStudent')?.value | number:'1.1-1') ?? '0.0'"
              readonly class="stat-input readonly" />
            <div class="field-helper">Calculated from total images ÷ approximate subjects. Actual numbers may vary.
            </div>
          </div>
        </div>
      </section>

      <!-- Section 2: Production Services -->
      <section class="section-card">
        <div class="section-header-row">
          <h3>
            <span>Production Services</span>
            <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
              <span class="tooltip-icon" (click)="toggleTooltip('productionServices', $event)">ⓘ</span>
              @if (activeTooltipKey === 'productionServices') {
              <span class="tooltip-bubble">{{ tooltips['productionServices'] }}</span>
              }
            </span>
          </h3>
        </div>
        <p class="section-subtitle">Select the services you want for this job. Additional options will appear for
          selected services.</p>

        <div class="services-list">
          <!-- Service 1: Smart Cull -->
          <div class="service-card" [class.selected]="form.get('pickBestImage')?.value">
            <div class="service-card-header" (click)="toggleService('pickBestImage')">
              <input type="checkbox" formControlName="pickBestImage" (click)="$event.stopPropagation()" />
              <div class="service-text">
                <div class="service-title-row">
                  <span class="service-name">Smart Cull (Pick Best Image)</span>
                  <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                    <span class="tooltip-icon" (click)="toggleTooltip('smartCull', $event)">ⓘ</span>
                    @if (activeTooltipKey === 'smartCull') {
                    <span class="tooltip-bubble">{{ tooltips['smartCull'] }}</span>
                    }
                  </span>
                </div>
                <div class="service-desc">Every image is reviewed by a person to select the best images, expressions,
                  and poses based on your preferences.</div>
              </div>
            </div>

            @if (form.get('pickBestImage')?.value) {
            <div class="service-options-panel" (click)="$event.stopPropagation()">
              <!-- Culling Method Selector -->
              <div class="culling-method-block">
                <div class="culling-method-header">
                  <span class="options-subheading">Culling Method</span>
                  <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                    <span class="tooltip-icon" (click)="toggleTooltip('cullingMethod', $event)">ⓘ</span>
                    @if (activeTooltipKey === 'cullingMethod') {
                    <span class="tooltip-bubble">{{ tooltips['cullingMethod'] }}</span>
                    }
                  </span>
                </div>

                <div class="culling-methods-grid">
                  <div class="method-option active">
                    <div class="radio-indicator checked"></div>
                    <div class="method-text">
                      <div class="method-title-row">
                        <strong>Professional Review</strong>
                        <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                          <span class="tooltip-icon" (click)="toggleTooltip('professionalReview', $event)">ⓘ</span>
                          @if (activeTooltipKey === 'professionalReview') {
                          <span class="tooltip-bubble">{{ tooltips['professionalReview'] }}</span>
                          }
                        </span>
                      </div>
                      <div class="method-subtitle">Every image is reviewed by a person.</div>
                    </div>
                  </div>

                  <div class="method-option disabled">
                    <div class="radio-indicator disabled"></div>
                    <div class="method-text">
                      <div class="method-title-row">
                        <strong>AI Smart Cull</strong>
                        <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                          <span class="tooltip-icon" (click)="toggleTooltip('aiSmartCull', $event)">ⓘ</span>
                          @if (activeTooltipKey === 'aiSmartCull') {
                          <span class="tooltip-bubble">{{ tooltips['aiSmartCull'] }}</span>
                          }
                        </span>
                        <span class="badge-coming-soon-inline">Coming Soon</span>
                      </div>
                      <div class="method-subtitle">Automated image selection based on your preferences.</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3-Column Settings -->
              <div class="culling-inputs-row">
                <div class="culling-input-col">
                  <label class="field-label">
                    <span>Images to Keep Per Subject *</span>
                    <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                      <span class="tooltip-icon" (click)="toggleTooltip('imagesToKeep', $event)">ⓘ</span>
                      @if (activeTooltipKey === 'imagesToKeep') {
                      <span class="tooltip-bubble">{{ tooltips['imagesToKeep'] }}</span>
                      }
                    </span>
                  </label>
                  <input type="number" formControlName="imagesPerStudent" min="0" placeholder="0 or more" class="stat-input" />
                  <div class="field-helper">Target number of finished images to keep for each subject. Actual number may
                    vary.</div>
                  @if (form.get('imagesPerStudent')?.hasError('required') && (form.get('imagesPerStudent')?.dirty || form.get('imagesPerStudent')?.touched)) {
                  <div class="field-error">Images to Keep Per Subject is required.</div>
                  }
                  @if (form.get('imagesPerStudent')?.hasError('min') && (form.get('imagesPerStudent')?.dirty || form.get('imagesPerStudent')?.touched)) {
                  <div class="field-error">Value cannot be negative.</div>
                  }
                </div>

                <div class="culling-input-col">
                  <label class="field-label">
                    <span>Images Per Unique Pose *</span>
                    <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                      <span class="tooltip-icon" (click)="toggleTooltip('imagesPerPose', $event)">ⓘ</span>
                      @if (activeTooltipKey === 'imagesPerPose') {
                      <span class="tooltip-bubble">{{ tooltips['imagesPerPose'] }}</span>
                      }
                    </span>
                  </label>
                  <input type="number" formControlName="imagePerUniquePose" min="0" placeholder="0 or more" class="stat-input" />
                  <div class="field-helper">Maximum number of images to keep from each unique pose or expression.</div>
                  @if (form.get('imagePerUniquePose')?.hasError('required') && (form.get('imagePerUniquePose')?.dirty || form.get('imagePerUniquePose')?.touched)) {
                  <div class="field-error">Images Per Unique Pose is required.</div>
                  }
                  @if (form.get('imagePerUniquePose')?.hasError('min') && (form.get('imagePerUniquePose')?.dirty || form.get('imagePerUniquePose')?.touched)) {
                  <div class="field-error">Value cannot be negative.</div>
                  }
                </div>

                <div class="culling-input-col">
                  <label class="field-label">
                    <span>Images Per Teacher / Staff *</span>
                    <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                      <span class="tooltip-icon" (click)="toggleTooltip('imagesPerTeacher', $event)">ⓘ</span>
                      @if (activeTooltipKey === 'imagesPerTeacher') {
                      <span class="tooltip-bubble">{{ tooltips['imagesPerTeacher'] }}</span>
                      }
                    </span>
                  </label>
                  <input type="number" formControlName="imagesPerTeacherIfPossible" min="0" placeholder="0 or more" class="stat-input" />
                  <div class="field-helper">Number of images to keep for teachers and staff members.</div>
                  @if (form.get('imagesPerTeacherIfPossible')?.hasError('required') && (form.get('imagesPerTeacherIfPossible')?.dirty || form.get('imagesPerTeacherIfPossible')?.touched)) {
                  <div class="field-error">Images Per Teacher / Staff is required (0 is accepted).</div>
                  }
                  @if (form.get('imagesPerTeacherIfPossible')?.hasError('min') && (form.get('imagesPerTeacherIfPossible')?.dirty || form.get('imagesPerTeacherIfPossible')?.touched)) {
                  <div class="field-error">Value cannot be negative.</div>
                  }
                </div>
              </div>

              <!-- Special Instructions -->
              <div class="special-instructions-block">
                <div class="special-instructions-header">
                  <label class="field-label">
                    <span>Special Instructions <span class="optional-tag">(optional)</span></span>
                    <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                      <span class="tooltip-icon" (click)="toggleTooltip('specialInstructions', $event)">ⓘ</span>
                      @if (activeTooltipKey === 'specialInstructions') {
                      <span class="tooltip-bubble">{{ tooltips['specialInstructions'] }}</span>
                      }
                    </span>
                  </label>
                </div>
                <textarea rows="3" formControlName="specialInstructions" maxlength="500"
                  placeholder="Enter any special instructions..." class="instructions-textarea"></textarea>
                <div class="char-count">{{ (form.get('specialInstructions')?.value || '').length }} / 500</div>
              </div>
            </div>
            }
          </div>

          <!-- Service 2: Enhancement -->
          <div class="service-card" [class.selected]="form.get('enhanceImages')?.value">
            <div class="service-card-header" (click)="toggleService('enhanceImages')">
              <input type="checkbox" formControlName="enhanceImages" (click)="$event.stopPropagation()" />
              <div class="service-text">
                <div class="service-title-row">
                  <span class="service-name">Enhancement (Image Correction and/or Retouching)</span>
                  <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                    <span class="tooltip-icon" (click)="toggleTooltip('enhancement', $event)">ⓘ</span>
                    @if (activeTooltipKey === 'enhancement') {
                    <span class="tooltip-bubble">{{ tooltips['enhancement'] }}</span>
                    }
                  </span>
                </div>
                <div class="service-desc">Choose your specific enhancement options on the following screen. Multiple
                  enhancement options are available. Professional image correction for natural color, clarity and
                  consistent results.</div>
              </div>
            </div>
          </div>

          <!-- Service 3: Background Removal -->
          <div class="service-card" [class.selected]="form.get('extractBackground')?.value">
            <div class="service-card-header" (click)="toggleService('extractBackground')">
              <input type="checkbox" formControlName="extractBackground" (click)="$event.stopPropagation()" />
              <div class="service-text">
                <div class="service-title-row">
                  <span class="service-name">Background Removal (Extraction)</span>
                  <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                    <span class="tooltip-icon" (click)="toggleTooltip('backgroundRemoval', $event)">ⓘ</span>
                    @if (activeTooltipKey === 'backgroundRemoval') {
                    <span class="tooltip-bubble">{{ tooltips['backgroundRemoval'] }}</span>
                    }
                  </span>
                </div>
                <div class="service-desc">Create clean subject extractions for background replacement.</div>
              </div>
            </div>
          </div>

          <!-- Service 4: Multi-Cropping -->
          <div class="service-card" [class.selected]="form.get('cropImages')?.value">
            <div class="service-card-header" (click)="toggleService('cropImages')">
              <input type="checkbox" formControlName="cropImages" (click)="$event.stopPropagation()" />
              <div class="service-text">
                <div class="service-title-row">
                  <span class="service-name">Multi-Cropping</span>
                  <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                    <span class="tooltip-icon" (click)="toggleTooltip('multiCropping', $event)">ⓘ</span>
                    @if (activeTooltipKey === 'multiCropping') {
                    <span class="tooltip-bubble">{{ tooltips['multiCropping'] }}</span>
                    }
                  </span>
                </div>
                <div class="service-desc">Create 1–4 finished compositions from each selected image. Set your studio's
                  crop specifications for each crop on the following screen.</div>
              </div>
            </div>

            @if (form.get('cropImages')?.value) {
            <div class="cropping-options-panel" (click)="$event.stopPropagation()">
              <div class="crops-per-image-row">
                <label class="field-label-inline">
                  <span>Crops Per Image *</span>
                  <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                    <span class="tooltip-icon" (click)="toggleTooltip('cropsPerImage', $event)">ⓘ</span>
                    @if (activeTooltipKey === 'cropsPerImage') {
                    <span class="tooltip-bubble">{{ tooltips['cropsPerImage'] }}</span>
                    }
                  </span>
                </label>
                <select formControlName="cropsPerImagePose" class="stat-input small-select">
                  <option [ngValue]="null" disabled selected>Select</option>
                  <option [ngValue]="1">1</option>
                  <option [ngValue]="2">2</option>
                  <option [ngValue]="3">3</option>
                  <option [ngValue]="4">4</option>
                </select>
              </div>
              @if (form.get('cropsPerImagePose')?.hasError('required') && (form.get('cropsPerImagePose')?.dirty || form.get('cropsPerImagePose')?.touched)) {
              <div class="field-error">Please select the number of Crops Per Image (1 to 4).</div>
              }
            </div>
            }
          </div>
        </div>
      </section>

      <!-- Section 3: Future Production Capabilities -->
      <section class="section-card coming-soon-card">
        <div class="section-header-row">
          <h3>
            <span>Future Production Capabilities</span>
            <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
              <span class="tooltip-icon" (click)="toggleTooltip('futureCapabilities', $event)">ⓘ</span>
              @if (activeTooltipKey === 'futureCapabilities') {
              <span class="tooltip-bubble">{{ tooltips['futureCapabilities'] }}</span>
              }
            </span>
          </h3>
          <span class="badge-coming-soon">Coming Soon</span>
        </div>
        <p class="section-subtitle">We're constantly expanding flwX with new production options. These capabilities will
          appear within the appropriate workflow steps as they become available.</p>
        <div class="pills-grid">
          @for (cap of futureProductionCapabilities; track cap) {
          <span class="pill-item">{{ cap }}</span>
          }
        </div>
      </section>

      <!-- Section 4: Future Image Delivery -->
      <section class="section-card coming-soon-card">
        <div class="section-header-row">
          <h3>
            <span>Future Image Delivery</span>
            <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
              <span class="tooltip-icon" (click)="toggleTooltip('futureDelivery', $event)">ⓘ</span>
              @if (activeTooltipKey === 'futureDelivery') {
              <span class="tooltip-bubble">{{ tooltips['futureDelivery'] }}</span>
              }
            </span>
          </h3>
          <span class="badge-coming-soon">Coming Soon</span>
        </div>
        <p class="section-subtitle">Direct gallery delivery to your favorite gallery platforms is on the roadmap.</p>
        <div class="pills-grid">
          @for (platform of futureImageDelivery; track platform) {
          <span class="pill-item">{{ platform }}</span>
          }
        </div>
      </section>

      <!-- Section 5: Estimated flwX Production -->
      <section class="section-card pricing-card">
        <div class="pricing-card-header">
          <h3>
            <span>Estimated flwX Production</span>
            <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
              <span class="tooltip-icon" (click)="toggleTooltip('estimatedProduction', $event)">ⓘ</span>
              @if (activeTooltipKey === 'estimatedProduction') {
              <span class="tooltip-bubble">{{ tooltips['estimatedProduction'] }}</span>
              }
            </span>
          </h3>
        </div>

        <div class="pricing-summary-container">
          <div class="pricing-metrics-left">
            <div class="pricing-metric-row">
              <span class="metric-label">Estimated Cost Of Job</span>
              <strong class="metric-value">${{ form.getRawValue().estimatedCostOfJob | number:'1.0-2' }}</strong>
            </div>

            <div class="pricing-metric-row">
              <span class="metric-label">
                <span>Estimated Production Per Subject</span>
                <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                  <span class="tooltip-icon" (click)="toggleTooltip('estimatedProductionPerSubject', $event)">ⓘ</span>
                  @if (activeTooltipKey === 'estimatedProductionPerSubject') {
                  <span class="tooltip-bubble">{{ tooltips['estimatedProductionPerSubject'] }}</span>
                  }
                </span>
              </span>
              <strong class="metric-value">${{ form.getRawValue().estimatedCostPerStudent | number:'1.0-2' }}</strong>
            </div>
          </div>

          <div class="pricing-toggle-right">
            <button type="button" class="view-pricing-btn" (click)="togglePricingDetails()">
              <span>View Pricing Details</span>
              <span class="tooltip-wrapper" (click)="$event.stopPropagation()">
                <span class="tooltip-icon" (click)="toggleTooltip('pricingDetails', $event)">ⓘ</span>
                @if (activeTooltipKey === 'pricingDetails') {
                <span class="tooltip-bubble">{{ tooltips['pricingDetails'] }}</span>
                }
              </span>
              <span class="chevron" [class.open]="showPricingDetails">⌄</span>
            </button>
          </div>
        </div>

        @if (showPricingDetails) {
        <div class="pricing-details-breakdown">
          <div class="pricing-details-header">
            <h4>Service Pricing Breakdown</h4>
          </div>
          <div class="cost-grid">
            <div class="cost-box">
              <span>Picking Charge</span>
              <div class="cost-input-wrap">
                <span>$</span>
                <input type="number" formControlName="pickingCharge" min="0" readonly />
              </div>
            </div>

            <div class="cost-box">
              <span>Crop</span>
              <div class="cost-input-wrap">
                <span>$</span>
                <input type="number" formControlName="cropCharge" min="0" readonly />
              </div>
            </div>

            <div class="cost-box">
              <span>Enhancement</span>
              <div class="cost-input-wrap">
                <span>$</span>
                <input type="number" formControlName="enhancementCharge" min="0" readonly />
              </div>
            </div>

            <div class="cost-box">
              <span>Extraction</span>
              <div class="cost-input-wrap">
                <span>$</span>
                <input type="number" formControlName="extractionCharge" min="0" readonly />
              </div>
            </div>
          </div>
        </div>
        }
      </section>

      @if (errorMessage) {
      <div class="empty-state error">{{ errorMessage }}</div>
      }

      @if (infoMessage) {
      <div class="empty-state info">{{ infoMessage }}</div>
      }

      <!-- Section 6: Footer Actions -->
      <section class="footer-actions-container">
        <button type="button" class="cancel-btn" (click)="onCancel()">
          {{ isEditMode() ? 'Back to Jobs' : 'Cancel' }}
        </button>
        @if (shouldShowNextButton()) {
        <button type="submit" class="submit-btn" [disabled]="isSaving">
          <span>{{ isSaving ? 'Saving...' : 'Continue' }}</span>
          @if (!isSaving) {
          <span class="arrow-icon">→</span>
          }
        </button>
        }
      </section>
    </form>
    }
  </div>

  @if (showValidationAlert) {
  <div class="modal-overlay" (click)="showValidationAlert = false">
    <div class="validation-alert-modal" (click)="$event.stopPropagation()">
      <div class="alert-modal-icon">⚠️</div>
      <h3>Please Fix the Following Errors</h3>
      <ul class="alert-error-list">
        @for (msg of validationAlertMessages; track msg) {
        <li>{{ msg }}</li>
        }
      </ul>
      <button type="button" class="alert-ok-btn" (click)="showValidationAlert = false">Got It</button>
    </div>
  </div>
  }
</div>